/**
 * Cypress / local E2E auth bypass: compile-time flag and runtime trust gate.
 * Side-effect free; safe to import from production route modules.
 */

import type { AuthTokenOverride } from 'app/contexts/AuthTokenContext'
import { isPlaygroundMode } from 'app/playground/mode'

export const CYPRESS_E2E_WINDOW_KEY = '__cypressE2E'

/**
 * True when the dev server or build sets `VITE_E2E_AUTH_TOKEN` (e.g. `pnpm dev:e2e`, CI Cypress build).
 */
export function isE2eMode(): boolean {
    return Boolean(import.meta.env.VITE_E2E_AUTH_TOKEN)
}

/** True when Cypress has set `window.__cypressE2E` before the app bundle loads. */
export function isCypressRuntime(): boolean {
    return typeof window !== 'undefined' && Boolean(window[CYPRESS_E2E_WINDOW_KEY as keyof Window])
}

/**
 * True when prequal experiment fields should be on without PostHog (financial-info step,
 * job start date for non-QC). Matches build-time E2E or Cypress on a loopback host only.
 */
export function isPrequalE2eExperimentsEnabled(): boolean {
    if (isE2eMode()) return true
    if (typeof window === 'undefined') return false
    return isCypressRuntime() && isLocalTrustHostname(window.location.hostname)
}

/** Loopback hostnames where Cypress / preview may run without Vite dev. */
export function isLocalTrustHostname(hostname: string): boolean {
    const h = hostname.toLowerCase()
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

/**
 * Clerk/auth override is only allowed in local-trust contexts so a mis-built bundle or DevTools
 * cannot skip Clerk on a real deployment host. Requires e2e intent (`VITE_E2E_AUTH_TOKEN` and/or
 * Cypress `window.__cypressE2E`) plus one of: Vite dev, localhost, or `VITE_ALLOW_E2E_AUTH_BYPASS=true`.
 * Never enabled when `VITE_REACT_APP_ENV` is `production`.
 */
export function isE2eAuthBypassAllowed(): boolean {
    const wantsBypass = isE2eMode() || isCypressRuntime()
    if (!wantsBypass) return false
    if (import.meta.env.VITE_REACT_APP_ENV === 'production') return false
    if (import.meta.env.DEV) return true
    if (import.meta.env.VITE_ALLOW_E2E_AUTH_BYPASS === 'true') return true
    if (typeof window === 'undefined') return false
    return isLocalTrustHostname(window.location.hostname)
}

/**
 * Bearer token for `AuthTokenProvider` when E2E bypass is allowed; otherwise `undefined`.
 */
export function getE2eAuthToken(): string | undefined {
    if (!isE2eAuthBypassAllowed()) return undefined
    return (import.meta.env.VITE_E2E_AUTH_TOKEN as string | undefined) ?? 'cypress-fake-jwt'
}

/**
 * Playground, Storybook (`AuthTokenProvider`), or gated E2E — not raw override on prod hosts.
 */
export function isUnsignedAuthMode(authTokenOverride: AuthTokenOverride | null): boolean {
    return isPlaygroundMode() || Boolean(authTokenOverride)
}
